/**
 * PlayerPreviewBreakdownModalContainer — gate for the in-place KPI breakdown
 * shown on top of the player preview modal (SE-5473).
 *
 * Renders nothing until a breakdown is open (a non-null slug) and a player id is
 * available; otherwise hands off to the View which mounts the breakdown screen
 * layered over the preview modal.
 * @remarks This is a Container component — owns the render gate.
 */

import PlayerPreviewBreakdownModalView from "./PlayerPreviewBreakdownModalView";

/**
 * Props for PlayerPreviewBreakdownModalContainer.
 */
export interface PlayerPreviewBreakdownModalContainerProps {
  /** Breakdown metric slug to render, or null when nothing is open. */
  readonly slug: string | null;
  /** Player id whose breakdown to render. */
  readonly playerId: string | undefined;
  /** Close handler — returns the user to the preview modal underneath. */
  readonly onClose: () => void;
}

/**
 * Gates the layered breakdown modal on an open slug + player id.
 * @param root0 - Container props
 * @param root0.slug - Breakdown metric slug, or null when nothing is open
 * @param root0.playerId - Player id whose breakdown to render
 * @param root0.onClose - Close handler returning to the preview modal
 * @returns The layered breakdown modal, or null when nothing is open
 */
const PlayerPreviewBreakdownModalContainer = ({
  slug,
  playerId,
  onClose,
}: PlayerPreviewBreakdownModalContainerProps) => {
  if (!slug || !playerId) return null;
  return (
    <PlayerPreviewBreakdownModalView
      slug={slug}
      playerId={playerId}
      onClose={onClose}
    />
  );
};

PlayerPreviewBreakdownModalContainer.displayName =
  "PlayerPreviewBreakdownModalContainer";

export default PlayerPreviewBreakdownModalContainer;
